import React from 'react';
import { IconChevronExpandYOutlineDuo18 } from 'nucleo-ui-outline-duo-18';

import { cn } from '@lyearn/style';
import { AvatarWithBadge } from '@lyearn/ui';
import UserMenu from '@/components/UserMenu';
import { useUserContext } from '@/modules/auth/hooks/useUser';

import { SidebarItemProps } from '../../hooks/useSidebar';
import { sidebarIconStrokeWidth } from './nav-icon';
import SidebarItem from './SidebarItem';
import {
  SIDEBAR_CARD,
  SIDEBAR_CARD_RAIL,
  SIDEBAR_LINE,
  SIDEBAR_TEXT_ACTIVE,
  SIDEBAR_TEXT_COLOR,
} from './theme';

const AccountCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { collapsed: boolean }
>(function AccountCard({ collapsed, className, ...rest }, ref) {
  const { name, emailId, profilePicture } = useUserContext();

  return (
    <div
      ref={ref}
      aria-label={collapsed ? 'Account menu' : undefined}
      className={cn(
        'relative flex h-64 w-full cursor-pointer items-center overflow-hidden rounded-12 text-left ring-1 ring-inset transition-all duration-300 ease-in-out',
        collapsed ? cn('px-8', SIDEBAR_CARD_RAIL) : cn('px-12', SIDEBAR_CARD),
        className,
      )}
      {...rest}>
      <span className="flex-shrink-0 transition-[width,height] duration-300 ease-in-out">
        <AvatarWithBadge
          name={name}
          size={collapsed ? 32 : 40}
          src={profilePicture}
          status="online"
        />
      </span>
      <span
        className={cn(
          'ms-8 flex min-w-0 flex-1 flex-col transition-opacity duration-150',
          collapsed && 'opacity-0',
        )}>
        <span
          className={cn('truncate text-[1.4rem] font-medium leading-[2rem]', SIDEBAR_TEXT_ACTIVE)}>
          {name}
        </span>
        <span className={cn('truncate text-[1.4rem] leading-[2rem]', SIDEBAR_TEXT_COLOR)}>
          {emailId}
        </span>
      </span>
      <IconChevronExpandYOutlineDuo18
        className={cn(
          'absolute right-12 top-12 h-16 w-16 transition-opacity duration-150',
          SIDEBAR_TEXT_COLOR,
          collapsed && 'opacity-0',
        )}
        strokeWidth={sidebarIconStrokeWidth(16)}
      />
    </div>
  );
});

interface SidebarFooterProps {
  items: SidebarItemProps[];
  collapsed: boolean;
  showAccount: boolean;
}

const SidebarFooter = ({ items, collapsed, showAccount }: SidebarFooterProps) => {
  if (!items.length && !showAccount) {
    return null;
  }

  return (
    <div className="flex flex-shrink-0 flex-col gap-12 pb-12">
      <div aria-hidden="true" className={cn('mx-12 border-t', SIDEBAR_LINE)} />
      {items.length ? (
        <div className="flex flex-col gap-4 px-12">
          {items.map((item) => (
            <SidebarItem key={item.id} collapsed={collapsed} sidebarItem={item} />
          ))}
        </div>
      ) : null}
      {showAccount ? (
        <div
          className={cn(
            'transition-[padding] duration-300 ease-in-out',
            collapsed ? 'px-4' : 'px-12',
          )}>
          <UserMenu
            overrides={{
              contentPosition: collapsed ? 'right-start' : 'top-start',
              trigger: <AccountCard collapsed={collapsed} />,
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(SidebarFooter);
